/**
 * Checking an unlock code, offline.
 *
 * The public half of the signing key is compiled into both apps, so a code is
 * verified with arithmetic and nothing else — no request, no account lookup,
 * no service that has to still be running in five years. The only moment
 * either app talks to the licence service is the one time it trades a store
 * receipt for a code.
 *
 * A code is deliberately copyable. For a one-off five-dollar tool the cost of
 * stopping that — accounts, device registration, a server that must stay up —
 * is worse than the leak it prevents.
 */
import { ed25519 } from '@noble/curves/ed25519.js';

import { asciiBytes, decodePayload, fromBase64url, splitToken, type License } from './token';

/** 32 raw bytes, hex, as printed by the key generation script. */
export type PublicKeyHex = string;

function hexBytes(hex: string): Uint8Array | null {
  if (hex.length % 2 !== 0 || !/^[0-9a-fA-F]*$/.test(hex)) return null;
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i += 1) out[i] = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/**
 * The claims a code carries if — and only if — it was signed by the holder of
 * the private key. Anything else, including a code whose payload was edited by
 * one character, comes back null.
 */
export function verifyToken(input: string, publicKeyHex: PublicKeyHex): License | null {
  const parts = splitToken(input);
  if (!parts) return null;

  const license = decodePayload(parts.payload);
  if (!license) return null;

  const signature = fromBase64url(parts.signature);
  if (!signature || signature.length !== 64) return null;

  const publicKey = hexBytes(publicKeyHex);
  if (!publicKey || publicKey.length !== 32) return null;

  try {
    return ed25519.verify(signature, asciiBytes(parts.payload), publicKey) ? license : null;
  } catch {
    return null;
  }
}
